import React, { useEffect, useRef, useState } from 'react';
import { GraduationCap, Star, Dna } from 'lucide-react';
import { TEAM, CONTACT_INFO } from '../mock';
import { useBooking } from '../context/BookingContext';

const TeamCard = ({ member, index, visible }) => {
  const isPrecision = member.id === 2; // Erika Tajra

  return (
    <div
      className={`group bg-white overflow-hidden border border-stone-200 hover:border-rose-300 hover:-translate-y-1.5 hover:shadow-xl transition-all duration-500 ease-out shadow-sm ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
      style={{ transitionDelay: `${index * 180}ms` }}
      data-testid={`team-card-${member.id}`}
    >
      {/* Image */}
      <div className="relative overflow-hidden h-80">
        <img
          src={member.image}
          alt={member.name}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-5">
          <h3
            className="text-white text-xl font-medium leading-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {member.name}
          </h3>
          <div className="flex items-center gap-2 mt-1">
            {isPrecision && <Dna className="w-3 h-3 text-rose-300 flex-shrink-0" />}
            <p className="text-rose-200 text-xs tracking-widest uppercase">{member.title}</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="inline-block bg-rose-50 text-rose-500 text-[10px] tracking-widest uppercase px-3 py-1 mb-4">
          {member.credentials}
        </div>
        <p className="text-stone-500 text-sm leading-relaxed mb-5 font-light">{member.bio}</p>

        {/* Specialties */}
        <div className="mb-5">
          <div className="text-stone-600 text-xs tracking-widest uppercase mb-2.5">
            {isPrecision ? 'Areas of Expertise' : 'Specialties'}
          </div>
          <div className="flex flex-wrap gap-2">
            {member.specialties.map((s, idx) => (
              <span key={idx} className="text-xs text-stone-500 border border-stone-200 px-3 py-1 hover:border-rose-300 hover:text-rose-500 transition-colors duration-200">
                {s}
              </span>
            ))}
          </div>
        </div>

        {/* Education */}
        <div className="flex items-start gap-2 text-stone-400 text-xs pt-4 border-t border-stone-100">
          <GraduationCap className="w-4 h-4 text-rose-400 flex-shrink-0 mt-0.5" />
          <span className="leading-relaxed">{member.education}</span>
        </div>
      </div>
    </div>
  );
};

const Team = () => {
  const { openBooking } = useBooking();
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="team" ref={ref} className="py-28 bg-stone-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">

        {/* Header */}
        <div
          className={`text-center mb-16 transition-all duration-1000 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="flex items-center justify-center gap-3 mb-5">
            <div className="w-8 h-px bg-rose-400" />
            <span className="text-rose-400 text-xs tracking-[0.4em] uppercase font-light">Our Experts</span>
            <div className="w-8 h-px bg-rose-400" />
          </div>
          <h2
            className="text-stone-800 mb-4"
            style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(2rem, 4vw, 3rem)' }}
          >
            Meet the Team
          </h2>
          <p className="text-stone-400 max-w-2xl mx-auto font-light text-lg leading-relaxed">
            A unique blend of aesthetic mastery and precision medicine — bringing together the best of both worlds under one roof.
          </p>
        </div>

        {/* 3-column team grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {TEAM.map((member, i) => (
            <TeamCard key={member.id} member={member} index={i} visible={visible} />
          ))}
        </div>

        {/* CTA Banner */}
        <div
          className={`bg-rose-50 border border-rose-100 p-10 text-center transition-all duration-1000 delay-500 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="flex justify-center gap-1 mb-3">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-4 h-4 fill-rose-400 text-rose-400" />
            ))}
          </div>
          <h3
            className="text-stone-800 text-2xl mb-3"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Ready to Transform?
          </h3>
          <p className="text-stone-500 font-light mb-6 max-w-md mx-auto text-sm leading-relaxed">
            Whether you are here for aesthetic enhancement or precision health optimisation, our team will design a personalised plan for you.
          </p>
          <button
            onClick={() => openBooking()}
            className="inline-block bg-rose-400 hover:bg-rose-500 text-white text-sm tracking-widest uppercase px-10 py-3 transition-colors duration-300"
          >
            Book a Consultation
          </button>
        </div>
      </div>
    </section>
  );
};

export default Team;
